import type { App } from 'vue';
import { defineRule, Field, Form, configure } from 'vee-validate';

import { isEmpty, numberFormatter } from '@libs/utils';

import { i18n } from '@/plugins/i18n';

import { MINIMUM_PAY_FAST_WITHDRAWAL_VALUE } from '@/modules/cash';

const { t } = i18n.global;

const install = (Vue: App) => {
  configure({
    validateOnBlur: false,
    validateOnChange: false,
    validateOnModelUpdate: true,
    validateOnInput: false,
  });

  defineRule('required', (value, [type]) => {
    if (isEmpty(value)) {
      return (
        t('misc.validations.not_empty_type', {
          type,
        }) || t('misc.validations.required')
      );
    }
    return true;
  });

  defineRule('between', (value, [min, max, type, unit = '']) => {
    if (max < value || value < min) {
      return t('misc.validations.x_between_y_z', {
        min: numberFormatter(min),
        max: numberFormatter(max),
        type,
        unit,
      });
    }

    return true;
  });

  defineRule('min', (value, [min, type, unit = '', minMessage = undefined]) => {
    if (value < Number(min)) {
      return t('misc.validations.x_at_least_y', {
        min:
          typeof minMessage !== 'undefined'
            ? numberFormatter(minMessage)
            : numberFormatter(min),
        type,
        unit,
      });
    }

    return true;
  });

  defineRule('equal', (value, [equal, type, unit = '', isPayFast = false]) => {
    if (isPayFast && equal < MINIMUM_PAY_FAST_WITHDRAWAL_VALUE) {
      return t('misc.validations.pay_fast');
    } else {
      if (Number(value) !== equal) {
        return t('misc.validations.x_equal_y', {
          equal: numberFormatter(equal),
          type,
          unit,
        });
      }
    }

    return true;
  });

  defineRule('priceTick', (value, [fixedPriceTick]) => {
    if (value % fixedPriceTick !== 0) {
      return fixedPriceTick === 10
        ? t('misc.validations.fixed_price_tick.last_digit_message')
        : t('misc.validations.fixed_price_tick.last_two_digits_message');
    }

    return true;
  });

  defineRule('quantityTick', (value, [quantityTick]) => {
    if (value % quantityTick !== 0) {
      return t('misc.validations.quantity_tick', {
        quantityTick,
      });
    }

    return true;
  });

  defineRule('max', (value, [max]) => {
    if (value > Number(max)) {
      return t('misc.validations.max', {
        max: numberFormatter(max),
      });
    }

    return true;
  });

  defineRule(
    'equalMin',
    (
      value,
      options: {
        min: number;
        isEqual?: boolean;
        message?: string;
        minMessage?: string | undefined;
        type?: string;
        unit?: string;
      }
    ) => {
      const {
        min,
        isEqual = true,
        message = '',
        minMessage = undefined,
        type,
        unit,
      } = options;

      const generatedMessage = message
        ? message
        : t('misc.validations.x_at_least_y', {
            min:
              typeof minMessage !== 'undefined'
                ? minMessage
                : numberFormatter(Number(min)),
            type,
            unit,
          });

      if (isEqual ? value <= Number(min) : value < Number(min)) {
        return generatedMessage;
      }

      return true;
    }
  );

  defineRule(
    'equalMax',
    (
      value,
      options: {
        max: number;
        isEqual?: boolean;
        message?: string;
        maxMessage?: string;
        type?: string;
        unit?: string;
      }
    ) => {
      const {
        max,
        isEqual = true,
        message = '',
        maxMessage = undefined,
        type,
        unit,
      } = options;

      const generatedMessage = message
        ? message
        : t('misc.validations.x_at_maximum_y', {
            max:
              typeof maxMessage !== 'undefined'
                ? maxMessage
                : numberFormatter(Number(max)),
            type,
            unit,
          });

      if (isEqual ? value >= Number(max) : value > Number(max)) {
        return generatedMessage;
      }

      return true;
    }
  );

  defineRule('upperBound', (value, [max, validationField, validatorField]) => {
    if (value > Number(max)) {
      return t('misc.validations.maxXY', {
        validationField,
        validatorField,
      });
    }

    return true;
  });

  defineRule('maxLength', (value, [max]) => {
    if (value.length > max) {
      return t('misc.validations.max_name_length', {
        max,
      });
    }

    return true;
  });

  defineRule('equalLength', (value, [equal, type, unit = '']) => {
    if (value.length !== equal) {
      return t('misc.validations.x_equal_y', {
        equal,
        type,
        unit,
      });
    }

    return true;
  });

  defineRule('duplicateValue', (value, [list, key, message]) => {
    if (key) {
      list = list.map((item) => item[key]);
    }

    const duplicateIndex = list?.findIndex((item) => item === value);

    if (duplicateIndex > -1) {
      return message;
    }

    return true;
  });

  Vue.component('VeeField', Field);
  Vue.component('VeeForm', Form);
};

export { install };
